import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { diasHasta, creditos, fecha } from "../lib/formato";
import { Aviso, Cabecera, Cargando, EstadoSocio, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import DialogoCobro from "../componentes/DialogoCobro";
import { useSesion } from "../lib/sesion";
import { useIdioma, type Variables } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface SocioPendiente {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  telefono: string | null;
  estado: string;
  fechaCaducidad: string | null;
  tipoSocio: { nombre: string; cuotaAnualCentimos: number | null } | null;
}

interface Pendientes {
  vencidas: SocioPendiente[];
  porVencer: SocioPendiente[];
}

/**
 * Enlace para avisar por WhatsApp: los moviles espanoles llevan prefijo 34.
 *
 * El mensaje va en el idioma de la pantalla: quien lo manda es quien atiende,
 * y el socio de un club catalan espera leerlo en catalan.
 */
function enlaceWhatsApp(t: Traductor, telefono: string, nombre: string, club: string): string {
  const numero = telefono.replace(/\D/g, "");
  const conPrefijo = numero.length === 9 ? `34${numero}` : numero;
  const texto = t("rn.mensajeWhatsapp", { nombre, club });
  return `https://wa.me/${conPrefijo}?text=${encodeURIComponent(texto)}`;
}

type Traductor = (clave: Clave, variables?: Variables) => string;

export default function Renovaciones() {
  const { t, cuenta, dias: enDias } = useIdioma();
  const { club, puede } = useSesion();
  const clienteConsultas = useQueryClient();
  const [dias, setDias] = useState(30);
  const [cobrando, setCobrando] = useState<SocioPendiente | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const pendientes = useQuery({
    queryKey: ["cuotas-pendientes", dias],
    queryFn: () => api<Pendientes>("/cuotas/pendientes", { query: { dias } }),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["cuotas-pendientes"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["panel"] });
  };

  return (
    <>
      <Cabecera titulo={t("rn.titulo")} descripcion={t("rn.descripcion")}>
        <select value={dias} onChange={(e) => setDias(Number(e.target.value))} style={{ width: 200 }}>
          {[15, 30, 60, 90].map((d) => (
            <option key={d} value={d}>
              {t("rn.vencenEnDias", { dias: enDias(d) })}
            </option>
          ))}
        </select>
        <BotonRecordatorios />
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      <div className="rejilla tres" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("rn.cuotaVencida")}
          valor={pendientes.data?.vencidas.length ?? "—"}
          color={pendientes.data && pendientes.data.vencidas.length > 0 ? "var(--rojo)" : undefined}
          pie={t("rn.noPuedenRetirar")}
        />
        <TarjetaDato
          etiqueta={t("rn.vencenEnDias", { dias: enDias(dias) })}
          valor={pendientes.data?.porVencer.length ?? "—"}
          color={pendientes.data && pendientes.data.porVencer.length > 0 ? "var(--ambar)" : undefined}
          pie={t("rn.convieneAvisar")}
        />
        <TarjetaDato
          etiqueta={t("rn.cuotaAnual")}
          valor={club?.cuotaAnualCentimos != null ? creditos(club.cuotaAnualCentimos) : "—"}
        />
      </div>

      {pendientes.isLoading || !pendientes.data ? (
        <Cargando />
      ) : (
        <div className="columna" style={{ gap: 16 }}>
          <Tabla
            titulo={t("rn.cuotaVencida")}
            socios={pendientes.data.vencidas}
            vacio={t("rn.nadieVencida")}
            alCobrar={puede("cobrarCuota") ? setCobrando : undefined}
            nombreClub={club?.nombre ?? t("rn.laAsociacion")}
          />
          <Tabla
            titulo={t("rn.vencenEnLosProximos", { dias: enDias(dias) })}
            socios={pendientes.data.porVencer}
            vacio={t("rn.ningunaVence", { dias: enDias(dias) })}
            alCobrar={puede("cobrarCuota") ? setCobrando : undefined}
            nombreClub={club?.nombre ?? t("rn.laAsociacion")}
          />
        </div>
      )}

      {cobrando && (
        <DialogoCobro
          socio={cobrando}
          importePorDefecto={(cobrando.tipoSocio?.cuotaAnualCentimos ?? club?.cuotaAnualCentimos ?? 2000) / 100}
          alCerrar={() => setCobrando(null)}
          alHecho={(hasta) => {
            setCobrando(null);
            setError("");
            setMensaje(
              t("rn.cobrada", {
                socio: `${cobrando.nombre} ${cobrando.apellidos}`,
                fecha: fecha(hasta),
              }),
            );
            refrescar();
          }}
          alFallar={(m) => {
            setCobrando(null);
            setMensaje("");
            setError(m);
          }}
        />
      )}
    </>
  );
}

function Tabla({
  titulo,
  socios,
  vacio,
  alCobrar,
  nombreClub,
}: {
  titulo: string;
  socios: SocioPendiente[];
  vacio: string;
  alCobrar?: (socio: SocioPendiente) => void;
  nombreClub: string;
}) {
  const { t, cuenta, dias: enDias } = useIdioma();
  return (
    <Panel titulo={titulo} sinCuerpo>
      {socios.length === 0 ? (
        <Vacio>{vacio}</Vacio>
      ) : (
        <div className="tabla-envoltorio">
          <table className="tabla">
            <thead>
              <tr>
                <th>{t("rn.socio")}</th>
                <th>{t("rn.telefono")}</th>
                <th>{t("rn.estado")}</th>
                <th>{t("rn.cuotaHasta")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {socios.map((s) => {
                const d = diasHasta(s.fechaCaducidad);
                return (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/socios/${s.id}`}>
                        #{s.numeroSocio} {s.nombre} {s.apellidos}
                      </Link>
                    </td>
                    <td className="pequeno">
                      {s.telefono ? (
                        <span className="fila" style={{ gap: 8 }}>
                          {s.telefono}
                          <a
                            href={enlaceWhatsApp(t, s.telefono, s.nombre, nombreClub)}
                            target="_blank"
                            rel="noreferrer"
                            className="etiqueta verde"
                          >
                            {t("rn.whatsapp")}
                          </a>
                        </span>
                      ) : (
                        <span className="suave">—</span>
                      )}
                    </td>
                    <td>
                      <EstadoSocio estado={s.estado} />
                    </td>
                    <td className="pequeno">
                      {s.fechaCaducidad ? fecha(s.fechaCaducidad) : <span className="suave">{t("rn.sinCuota")}</span>}
                      {d !== null && (
                        <div className={d < 0 ? "" : "suave"} style={d < 0 ? { color: "var(--rojo)" } : undefined}>
                          {d < 0
                            ? t("rn.vencioHace", { dias: enDias(-d) })
                            : d === 0
                              ? t("rn.venceHoy")
                              : t(d === 1 ? "rn.queda" : "rn.quedan", { dias: enDias(d) })}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {alCobrar && (
                        <button className="boton" onClick={() => alCobrar(s)}>
                          {t("rn.cobrarCuota")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/**
 * Manda los recordatorios de cuota en el momento, sin esperar al repaso diario.
 * Sirve tanto para el club que no lo tiene encendido como para el que quiere
 * adelantarlo.
 */
function BotonRecordatorios() {
  const { t, cuenta } = useIdioma();
  const [resultado, setResultado] = useState<string>("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar() {
    setError("");
    setResultado("");
    setEnviando(true);
    try {
      const r = await api<{ avisados: number; porCorreo: number; porMovil: number; sinCorreo: number }>(
        "/cuotas/recordatorios",
        { metodo: "POST" },
      );
      setResultado(
        r.avisados === 0
          ? t("rn.nadieAQuienAvisar")
          : t(r.avisados === 1 ? "rn.avisadoUno" : "rn.avisados", {
              n: r.avisados,
              porCorreo: r.porCorreo,
              porMovil: r.porMovil,
            }) + (r.sinCorreo > 0 ? ` ${cuenta(r.sinCorreo, "rn.unoSinCorreo", "rn.sinCorreo")}` : ""),
      );
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("rn.falloRecordatorios"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <button className="boton secundario" onClick={() => void enviar()} disabled={enviando}>
        {enviando ? t("rn.enviando") : t("rn.avisar")}
      </button>
      {(resultado || error) && (
        <div style={{ flexBasis: "100%", marginTop: 8 }}>
          <Aviso tipo="error">{error}</Aviso>
          <Aviso tipo="ok">{resultado}</Aviso>
        </div>
      )}
    </>
  );
}
